/**
 * WHAT EACH PIECE OF A PLAN IS, AND HOW IT WORKS — the one plain-words source for every module's
 * "Explain this" (owner, 24 Oct 2026: "when i clicked on the Explain this, it doesnt actually explain what
 * it is, how it works. I feel like there should be a deeper dive explanation of what this solution
 * actually is").
 *
 * Keyed by the plan-piece kinds every module already reads its plans into for "What could go wrong"
 * (RiskKind, strategy/strategyRisks.ts), so a module explains a plan with the same list it warns about.
 * Each entry: a plain title; what it is (one or two sentences); how it works (three or four short
 * points); and what is worth knowing (the limits and costs, two or three points). The risks themselves
 * stay in "What could go wrong"; this says what the thing is.
 *
 * Figures come from the app's own constants, never typed in, so a rate change moves the words with it.
 * Support at Home's card and advice-document voice (sahStrategyExplainers.ts) reads its "what it is",
 * "how it works" and "worth knowing" from here where the two describe the same thing.
 */
import type { RiskKind } from '../../strategy/strategyRisks.ts';
import { formatCurrency } from '../../utils/index.ts';
import {
    DOWNSIZER_CONTRIBUTION_CAP,
    FUNERAL_BOND_LIMIT,
    GIFTING_LIMIT_5_YEAR,
    GIFTING_LIMIT_ANNUAL,
    NON_CONCESSIONAL_CONTRIBUTION_CAP,
} from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { HEAS_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';

export interface LeverExplainer {
    /** The everyday name, e.g. "The Home Equity Access Scheme". */
    title: string;
    /** What it is, in one or two sentences. */
    what: string;
    /** How it works, in three or four short points. */
    how: string[];
    /** Limits and costs worth knowing, in two or three points. */
    worthKnowing: string[];
}

const annuityCounting = 'Centrelink counts 60% of the price for the first five years, or until age 85 if that is later, and 30% after that. Of each payment, 60% counts as income.';

export const LEVER_EXPLAINERS: Partial<Record<RiskKind, LeverExplainer>> = {
    heas: {
        title: 'The Home Equity Access Scheme',
        what: 'The government’s own version of a reverse mortgage: a loan against the home, paid out as regular fortnightly amounts (or a lump sum) on top of the Age Pension, while you keep living there.',
        how: [
            'You apply to Services Australia; the loan is secured against the home.',
            `Nothing is repaid month to month. Interest, at the government’s rate (currently ${HEAS_RATE}% a year), is added to the loan.`,
            'The payments are not counted as income for the Age Pension, so taking them does not reduce it.',
            'The loan and its interest are repaid when the home is sold, usually from the estate. You can stop, start or repay at any time.',
        ],
        worthKnowing: [
            'It is still a loan: the interest compounds, so what the home leaves to the family falls the longer it runs.',
            'The Age Pension and the scheme’s payments together are capped at 150% of the maximum pension.',
            'You can never owe more than the home is worth.',
        ],
    },
    reverseMortgage: {
        title: 'A reverse mortgage',
        what: 'A loan from a bank or lender against the home, paid as a lump sum or in drawdowns, while you keep living there. Nothing is repaid while you live in the home.',
        how: [
            'The lender pays the money out; the loan is secured against the home.',
            'Interest is added to the loan each month instead of being paid.',
            'The loan and its interest are repaid when the home is sold, usually from the estate.',
            'Money from the loan counts as an asset while it sits in the bank.',
        ],
        worthKnowing: [
            'Interest compounds on a growing balance, and lenders’ rates are higher than the government scheme’s.',
            'You can never owe more than the home is worth (the no-negative-equity rule).',
            'Terms differ between lenders and need comparing.',
        ],
    },
    gift: {
        title: 'A gift to family',
        what: 'Money given to family now, so it stops being yours and stops counting for the Age Pension and, where care is means-tested, what you pay for it.',
        how: [
            `Up to ${formatCurrency(GIFTING_LIMIT_ANNUAL)} a year, and no more than ${formatCurrency(GIFTING_LIMIT_5_YEAR)} over five years, stops counting straight away.`,
            'Anything above those limits still counts as yours for five years from the gift.',
            'Once given, the money belongs to the person who received it.',
        ],
        worthKnowing: [
            'It cannot be taken back if you need it later.',
            'A gift is the family’s inheritance received early, so it is no longer in the estate.',
        ],
    },
    funeralBond: {
        title: 'A funeral bond',
        what: 'Money set aside with a friendly society or insurer that can only be used to pay for the funeral.',
        how: [
            `Up to ${formatCurrency(FUNERAL_BOND_LIMIT)} each is left out of the assets test, and its earnings are not counted as income.`,
            'The bond is paid out to the funeral director, or to the estate to cover the funeral.',
            'A couple can each have one.',
        ],
        worthKnowing: [
            'The money cannot be used for anything else.',
            'An amount above the limit is counted in full.',
        ],
    },
    annuityNil: {
        title: 'A lifetime income (lifetime annuity)',
        what: 'A lump sum handed to an insurer in return for an income paid for the rest of your life, however long that is.',
        how: [
            'You choose the amount; the insurer sets the income from your age and its rates.',
            'The income is paid regularly for life, on top of the Age Pension.',
            annuityCounting,
            'Nothing comes back to the family when you die.',
        ],
        worthKnowing: [
            'Most of the money stops being available to spend once it is handed over.',
            'Its value depends on how long you live: a short life returns less than was paid.',
        ],
    },
    annuityIndexed: {
        title: 'A lifetime income that rises with inflation',
        what: 'A lifetime annuity whose income goes up each year with prices, so it keeps its buying power.',
        how: [
            'A lump sum is handed to an insurer for an income paid for life.',
            'The income starts lower than a fixed one, then rises each year with inflation.',
            annuityCounting,
        ],
        worthKnowing: [
            'Most of the money stops being available to spend once it is handed over.',
            'It takes some years for the rising income to catch up with a fixed one.',
        ],
    },
    annuityReturned: {
        title: 'A lifetime income with money back',
        what: 'A lifetime annuity that also returns part of the price to the family if you die in the early years.',
        how: [
            'A lump sum is handed to an insurer for an income paid for life.',
            'For about the first half of your life expectancy, the full price comes back to the family at death; after that, a falling amount, then nothing.',
            annuityCounting,
        ],
        worthKnowing: [
            'The income is a little lower than one with no money back.',
            'The money-back amount can be drawn out for a while, but it falls each year to nothing.',
        ],
    },
    annuityGuaranteed: {
        title: 'A guaranteed income with the full amount back',
        what: 'A lifetime income combined with life cover, so the full amount goes back to the family whenever death comes.',
        how: [
            'Part of the money buys a lifetime income; the rest pays for life cover.',
            'The income is paid for life, on top of the Age Pension.',
            'At death the full amount goes back to the family.',
            'Only the income part gets the lifetime-income treatment; the life cover is counted in full.',
        ],
        worthKnowing: [
            'The income is lower than a plain lifetime annuity of the same price.',
            'The insurer sets who can buy it, by age and health.',
        ],
    },
    clearDebt: {
        title: 'Paying off a debt',
        what: 'Using savings to clear a loan or credit card, so its repayments and interest stop.',
        how: [
            'The repayment comes out of savings; the debt and its repayments disappear.',
            'Less money is counted for the Age Pension and, where care is means-tested, what you pay for it.',
            'The interest saved is a sure return, equal to the loan’s rate.',
        ],
        worthKnowing: [
            'The money is gone from savings, so enough must stay in the bank for emergencies.',
            'Where a loan’s rate is low, keeping the money invested can leave more.',
        ],
    },
    renovate: {
        title: 'Spending on the home',
        what: 'Money moved from savings into the home you live in, by improving it or buying a dearer one. The home you live in is left out of the Age Pension assets test.',
        how: [
            'The money leaves the bank, where it was counted, and becomes home value, which is not.',
            'Less counted can mean more Age Pension and, where care is means-tested, less to pay for it.',
            'The value stays in the family’s hands, in the house.',
        ],
        worthKnowing: [
            'Money in the home cannot be spent day to day; it comes back only if the home is sold or borrowed against.',
            'Work on a home does not always add its full cost to what the home is worth.',
        ],
    },
    sellHome: {
        title: 'Selling the family home',
        what: 'The home is sold and the money used for what comes next: a new home, a room or unit, and what is left over.',
        how: [
            'Selling costs come off the sale price first.',
            'Money spent on a new home you live in stays out of the assets test.',
            'Money left over is counted in full, unless it goes into super under the downsizer rule.',
        ],
        worthKnowing: [
            'Selling and moving cost money and effort that does not come back.',
            'More counted money can mean less Age Pension, even with more cash to hand.',
        ],
    },
    sellAndRent: {
        title: 'Selling and renting instead',
        what: 'The home is sold and no new home is bought; the money is kept and rent is paid from it.',
        how: [
            'The whole sale, after costs, becomes money Centrelink counts.',
            'Renters are assessed as non-homeowners, who have higher asset limits.',
            'Rent is paid each year from savings and the Age Pension.',
        ],
        worthKnowing: [
            'Rent rises over time and is never recovered.',
            'There is no home left for the family; the estate is what the savings leave.',
        ],
    },
    rentHome: {
        title: 'Keeping the home and renting it out',
        what: 'The home is kept and let to tenants while care or the new arrangement is paid for another way.',
        how: [
            'The rent, after costs, counts as income for the Age Pension and is taxed.',
            'For the first two years after leaving, the home is still treated as the home and not counted; after that it counts as an asset.',
            'The home stays in the family’s hands.',
        ],
        worthKnowing: [
            'Rent can stop between tenants while rates, insurance and repairs go on.',
            'Selling later may bring capital gains tax.',
        ],
    },
    rentFormerHome: {
        title: 'Renting out the former home',
        what: 'The old home is kept and let to tenants after the move.',
        how: [
            'The rent, after costs, counts as income for the Age Pension and is taxed.',
            'The former home is counted as an asset once it is no longer the home.',
            'It stays in the family’s hands and can be sold later.',
        ],
        worthKnowing: [
            'Rent can stop between tenants while the costs go on.',
            'A later sale may bring capital gains tax.',
        ],
    },
    keepHomeInCare: {
        title: 'Keeping the home',
        what: 'The home is kept, not sold or let, while care is paid for from other money.',
        how: [
            'The home stays out of the assets test for two years after leaving it, or while a partner or protected person still lives there.',
            'For aged care fees, its value is counted only up to a capped amount.',
            'It stays in the family’s hands.',
        ],
        worthKnowing: [
            'An empty home still costs rates, insurance and upkeep.',
            'After two years it starts counting in full for the Age Pension.',
        ],
    },
    sellInvestmentProperty: {
        title: 'Selling an investment property',
        what: 'A property owned to rent out is sold, and the money used for the plan.',
        how: [
            'Selling costs and any capital gains tax come off the sale first.',
            'The rent stops counting as income.',
            'What is left is counted as money in the bank, or pays for the room or unit.',
        ],
        worthKnowing: [
            'The rent income is gone.',
            'Capital gains tax can be large on a property held a long time.',
        ],
    },
    transferHome: {
        title: 'Signing the home over for the right to live there',
        what: 'The home is transferred to family in return for a lifelong right to keep living there (a granny flat right).',
        how: [
            'The home leaves your name; in return you hold the right to live in it for life.',
            'Centrelink takes the home at face value: its value is the price of the right, and none of it is a gift, whatever it is worth. Only cash given on top is tested.',
            'If the home is worth more than Centrelink’s homeowner line, you stay homeowners for the Age Pension and the right is not counted; at or below it, you are not homeowners and the right counts as an asset.',
        ],
        worthKnowing: [
            'The home belongs to the family from then on, including if they sell, separate or go bankrupt.',
            'Stamp duty may be payable on the transfer, usually by the family member; it is not included in these figures.',
            'Leaving within five years can lead Centrelink to look at the transfer again as a gift.',
            'A written agreement drawn up by a solicitor protects everyone.',
        ],
    },
    grannyFlat: {
        title: 'A granny flat right',
        what: 'A lifelong right to live in a family member’s home, paid for with cash, by paying for a build there, or by signing your home over.',
        how: [
            'You pay for the right to live there for the rest of your life.',
            'Paying for a build, or signing your home over, is taken at face value: never a gift on its own, whatever it costs.',
            'Cash is tested against Centrelink’s limit for your age: up to it, nothing is a gift; anything above it (or above the build or home it comes on top of) counts as a gift for five years.',
            'Give more than Centrelink’s homeowner line and you stay homeowners, with the right not counted; give less, and the right counts as an asset.',
        ],
        worthKnowing: [
            'The money, the build or the home belongs to the family once it is handed over.',
            'A written agreement drawn up by a solicitor protects everyone if circumstances change.',
        ],
    },
    grannyFlatBuild: {
        title: 'Paying for a build at their home',
        what: 'You pay to build or extend your own accommodation at a family member’s home, in return for a lifelong right to live there.',
        how: [
            'You pay the builder; the building becomes part of their property.',
            'Centrelink takes the cost of the build at face value: it is the price of the right, never a gift on its own, whatever it costs.',
            'Any cash given on top is tested: above the greater of the build and Centrelink’s limit for your age, it is a gift for five years.',
            'Give more than Centrelink’s homeowner line in all and you stay homeowners, with the right not counted; give less, and the right counts as an asset.',
        ],
        worthKnowing: [
            'Approvals, delays and over-runs are common; keep the contract and invoices.',
            'The building belongs to the family member, exposed to their divorce, debts or death.',
            'A written agreement drawn up by a solicitor protects everyone.',
        ],
    },
    keepHomeEmpty: {
        title: 'Keeping your home, empty',
        what: 'Your home is kept as it is after you move, with no one living in it.',
        how: [
            'It stops being exempt from the day you move: Centrelink counts it in full as an asset.',
            'It earns nothing, but it is still yours, and it keeps growing in value.',
        ],
        worthKnowing: [
            'Rates, insurance and upkeep go on; they are not in these figures unless entered.',
            'It usually lowers the Age Pension more than anything else in the plan.',
        ],
    },
    moveInOnly: {
        title: 'Moving in with family, with nothing paid',
        what: 'You move into a family member’s home without paying for a right to live there.',
        how: [
            'No money changes hands, so nothing is counted as a gift.',
            'Your own money stays yours and is counted as before, and a home you keep stops being exempt the day you move: it is counted from then.',
        ],
        worthKnowing: [
            'There is no legal right to stay if circumstances change.',
        ],
    },
    villageEntry: {
        title: 'A retirement village unit',
        what: 'You pay an entry contribution for the right to live in a village unit, plus ongoing fees, and part of the entry contribution is kept by the village when you leave.',
        how: [
            'The entry contribution is paid up front, often from selling the home.',
            'Monthly fees cover the village’s running costs.',
            'When you leave, the village keeps a departure fee, which grows with each year you stay, and refunds the rest.',
            'Whether the entry contribution counts as an asset depends on its size against Centrelink’s threshold.',
        ],
        worthKnowing: [
            'The refund can take months to arrive after leaving.',
            'Contracts differ a lot between villages; the contract decides what comes back.',
        ],
    },
    radPaid: {
        title: 'Paying the room deposit (RAD)',
        what: 'A refundable accommodation deposit: the room’s price, paid up front, which comes back when care ends.',
        how: [
            'The deposit is paid to the home, from savings or the sale of the home.',
            'It is not counted in the Age Pension assets test, though it is counted for the aged care means test.',
            'Paying it means no daily payment on the part paid.',
            'When care ends it is refunded, less 2% a year kept by the home for the first five years and anything drawn from it.',
        ],
        worthKnowing: [
            'Money in the deposit earns nothing and cannot be spent.',
            'Fees can be drawn from the deposit, but that lowers the refund and raises the daily payment.',
        ],
    },
    dap: {
        title: 'The daily payment for the room (DAP)',
        what: 'A daily accommodation payment: a charge for the part of the room’s price not paid up front, like interest at the government’s set rate.',
        how: [
            'It is charged each day on the unpaid part of the room price.',
            'The rate is set by the government and changes each quarter.',
            'It can be paid each month, or drawn from a deposit.',
        ],
        worthKnowing: [
            'It never comes back.',
            'Money not used for a deposit stays yours and can be invested or spent.',
        ],
    },
    fromSavings: {
        title: 'Paying from savings',
        what: 'The cost is paid from bank accounts, term deposits, super pensions or shares.',
        how: [
            'The accounts named in the plan are drawn down, in the order shown.',
            'Money that was counted by Centrelink becomes a deposit or payment that may not be.',
            'Less money is left earning interest.',
        ],
        worthKnowing: [
            'Enough must stay in the bank for emergencies.',
            'Selling shares can bring capital gains tax.',
        ],
    },
    superContribution: {
        title: 'Putting money into super',
        what: 'Savings moved into super, where the earnings are taxed less, or not at all once it pays a pension.',
        how: [
            `From selling the home, each person aged 55 or over can put in up to ${formatCurrency(DOWNSIZER_CONTRIBUTION_CAP)} under the downsizer rule.`,
            `Otherwise, up to ${formatCurrency(NON_CONCESSIONAL_CONTRIBUTION_CAP)} a year can go in after tax, while the age and balance rules allow.`,
            'Inside super, earnings are taxed at no more than 15%, and nothing once it pays a pension.',
        ],
        worthKnowing: [
            'Once you are over Age Pension age, super counts for the Age Pension like other savings.',
            'Money in super can be taken out, but there are rules on how.',
        ],
    },
    accountBasedPension: {
        title: 'An account-based pension',
        what: 'Super turned into a regular income, paid from your own account.',
        how: [
            'You choose how much to draw, above a yearly minimum set by your age.',
            'Earnings inside the account are not taxed, and payments from age 60 are tax-free.',
            'It counts for the Age Pension like other savings.',
        ],
        worthKnowing: [
            'The income lasts only as long as the balance does.',
            'The balance moves with the investments.',
        ],
    },
    investmentBond: {
        title: 'An investment bond',
        what: 'An investment that pays its own tax inside, so nothing goes on your tax return, and becomes tax-free after ten years.',
        how: [
            'Tax of 30% is paid inside the bond on its earnings.',
            'After ten years, what comes out is tax-free.',
            'Centrelink counts it like the savings it replaces.',
        ],
        worthKnowing: [
            'Taking money out before ten years can cost some of the tax benefit.',
            'Its value moves with the investments.',
        ],
    },
    shares: {
        title: 'Shares or managed funds',
        what: 'Money invested in a spread of shares or funds, which can be sold at any time.',
        how: [
            'It is counted in full as an asset.',
            'Centrelink deems it to earn a set rate, whatever it actually pays.',
            'Dividends and growth are taxed.',
        ],
        worthKnowing: [
            'Values move, sometimes sharply.',
            'Selling can bring capital gains tax.',
        ],
    },
    investmentProperty: {
        title: 'An investment property',
        what: 'A property bought or kept to rent out.',
        how: [
            'It is counted in full as an asset.',
            'The rent, after costs, counts as income and is taxed.',
        ],
        worthKnowing: [
            'It cannot be sold in part, and selling takes time and money.',
            'Rent can stop while the costs go on.',
        ],
    },
};

/** The explainers for a plan's pieces, in order, each once; pieces with none (doing nothing) are left out. */
export function leverExplainersFor(kinds: ReadonlyArray<RiskKind | string | null | undefined>): Array<{ kind: RiskKind; explainer: LeverExplainer }> {
    const seen = new Set<string>();
    const out: Array<{ kind: RiskKind; explainer: LeverExplainer }> = [];
    for (const k of kinds) {
        if (!k || seen.has(k)) continue;
        seen.add(k);
        const explainer = LEVER_EXPLAINERS[k as RiskKind];
        if (explainer) out.push({ kind: k as RiskKind, explainer });
    }
    return out;
}
